import React from 'react';
import { Heart, Users, MessageCircle, Mail, Globe } from 'lucide-react';
import { Logo } from '../components/ui/Logo';
import { UnifiedHeader } from '../components/layout';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';
import { REUNION } from '../config/organization';

const ContactUsPageHeritage: React.FC = () => {
 return (
 <div className="min-h-screen bg-surface-1 text-text-primary">
 <SEO title={PAGE_META['/contact'].title} description={PAGE_META['/contact'].description} path="/contact" />
 <UnifiedHeader variant="heritage" currentPage="Contact Us" />

 {/* Hero Section */}
 <section className="relative h-96 flex items-center justify-center overflow-hidden mt-16">
 <div className="absolute inset-0">
 <div className="absolute inset-0 bg-black/40 z-10"></div>
 <div
 className="w-full h-full bg-cover transform scale-110"
 style={{
 backgroundImage: "url('/images/family-hero.jpeg')",
 backgroundPosition: '50% 80%',
 filter: 'blur(0.5px)'
 }}
 ></div>
 </div>

 <div className="relative z-20 text-center max-w-4xl px-4">
 <p className="text-white/80 text-sm font-medium mb-4">Contact Us</p>
 <h1 className="text-4xl md:text-6xl font-display font-semibold mb-6 text-white leading-tight tracking-wide" style={{
 textShadow: '2px 2px 6px rgba(0,0,0,0.8)',
 fontFamily: 'Georgia, "Times New Roman", serif',
 letterSpacing: '0.02em'
 }}>
 Get in Touch
 </h1>
 <p className="text-lg text-white leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
 We're here to help with any questions, concerns, or ways you'd like to get more involved with the Redd Family Association.
 </p>
 </div>
 </section>

 {/* Contact Methods Section */}
 <section className="py-16 bg-surface-1 relative overflow-hidden">
 <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
 {/* Email Us */}
 <div className="bg-surface-1 rounded-lg p-8 border border-border shadow-sm text-center">
 <div className="w-16 h-16 bg-brand rounded-lg flex items-center justify-center mx-auto mb-6">
 <Mail className="w-8 h-8 text-white" />
 </div>

 <h3 className="text-xl font-bold mb-3 text-text-primary font-display">
 Email Us
 </h3>

 <p className="text-text-primary text-sm leading-relaxed mb-4">
 Send us a message and we'll respond within 24 hours
 </p>

 <div className="text-sm font-semibold mb-6 text-text-secondary">
 placeholder@thereddfamily.org
 </div>

 <a
 href="mailto:placeholder@thereddfamily.org"
 className="w-full px-4 py-2 bg-brand text-white rounded-full text-sm font-semibold transition-transform inline-block text-center"
 >
 Send Email
 </a>
 </div>

 {/* Follow Us */}
 <div className="bg-surface-1 rounded-lg p-8 border border-border shadow-sm text-center">
 <div className="w-16 h-16 bg-brand rounded-lg flex items-center justify-center mx-auto mb-6">
 <Globe className="w-8 h-8 text-white" />
 </div>

 <h3 className="text-xl font-bold mb-3 text-text-primary font-display">
 Follow Us
 </h3>

 <p className="text-text-primary text-sm leading-relaxed mb-4">
 Connect with us on Instagram for updates, photos, and family news
 </p>

 <div className="text-sm font-semibold mb-6 text-text-secondary">
 @thereddfamily
 </div>

 <a
 href="https://www.instagram.com/thereddfamily"
 target="_blank"
 rel="noopener noreferrer"
 className="w-full px-4 py-2 bg-brand text-white rounded-full text-sm font-semibold transition-transform inline-block text-center"
 >
 Follow Us on Instagram
 </a>
 </div>
 </div>
 </div>
 </section>

 {/* TODO: "Send Us a Message" contact form
 * Requires a publicly accessible backend API endpoint (no auth).
 * Before enabling, implement:
 * - Rate limiting (per-IP and global)
 * - CAPTCHA or honeypot spam protection
 * - Input sanitization
 * - Email delivery via SES
 */}

 {/* FAQ Section */}
 <section className="py-16 bg-surface-1 relative overflow-hidden">
 <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-10">
 <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-surface-1 bg-clip-text text-transparent font-display">
 Frequently Asked Questions
 </h2>
 <p className="text-xl text-text-secondary max-w-3xl mx-auto">
 Find quick answers to common questions about our family association
 </p>
 </div>

 <div className="max-w-2xl mx-auto">
 <div className="grid gap-8">
 {[
 {
 question: "How do I join the Redd Family Association?",
 answer: "Membership is open to all descendants of the Redd family. Contact our membership coordinator to begin the registration process and verify your family connection."
 },
 {
 question: "When is the next family reunion?",
 answer: `Our next family reunion is a ${REUNION.durationLabel} cruise aboard the ${REUNION.cruiseLine}, sailing ${REUNION.dateLabel} round-trip from ${REUNION.departurePort}. Check our news section and join our newsletter for the latest updates and booking information.`
 },
 {
 question: "How can I contribute to the family?",
 answer: "We welcome contributions in many forms — volunteering, sharing family stories, or financial support. An online payment option will be available soon. In the meantime, send us an email to learn how you can give back."
 }
 ].map((faq, index) => {
 return (
 <div key={index} className="bg-surface-1 rounded-lg p-6 border border-border shadow-sm">
 <h3 className="text-lg font-bold mb-3 text-text-primary font-display">
 {faq.question}
 </h3>
 <p className="text-text-primary leading-relaxed">
 {faq.answer}
 </p>
 </div>
 );
 })}
 </div>
 </div>
 </div>
 </section>

 {/* Footer */}
 <footer className="bg-surface-1 border-t border-border py-16">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <Logo size="lg" className="mx-auto mb-8 shadow-sm border-4 border-border" />

 <h3 className="text-2xl font-bold mb-4 text-text-primary font-display">
 The Redd Family Association
 </h3>

 <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
 Where faith guides our path, family strengthens our hearts, and every generation is blessed in our continuing legacy.
 </p>

 <div className="flex justify-center space-x-8 text-text-tertiary">
 {[Heart, Users, MessageCircle, Mail].map((Icon, index) => (
 <Icon key={index} className="w-6 h-6 hover:text-brand transition-colors cursor-pointer" />
 ))}
 </div>
 </div>
 </footer>
 </div>
 );
};

export default ContactUsPageHeritage;
